import { createContext, useState, useEffect, useMemo } from "react";
import { extendTheme } from "@chakra-ui/react";
import axios from "axios";

const ThemeContext = createContext();

export const ThemeProvider = ({ children, subdomain }) => {
    const [themeData, setThemeData] = useState(null);
    const [logo, setLogo] = useState(null);
    const [businessName, setBusinessName] = useState(null);
    const [loadingTheme, setLoadingTheme] = useState(true);
    const [errorTheme, setErrorTheme] = useState(null);

    const isProduction = process.env.NODE_ENV === "production";
    const API_BASE_URL = isProduction
        ? process.env.REACT_APP_API_BASE_URL_PROD
        : process.env.REACT_APP_API_BASE_URL_DEV;

    useEffect(() => {
        const fetchTheme = async () => {
            if (!subdomain) {
                setLoadingTheme(false);
                return;
            }

            try {
                setLoadingTheme(true);
                const response = await axios.get(
                    `${API_BASE_URL}/themeing/my-theme/${subdomain}`
                );

                const { theme, logo, businessName } = response.data;

                setThemeData(theme);
                setLogo(logo);
                setBusinessName(businessName);
                setErrorTheme(null);
            } catch (err) {
                console.error("Error fetching theme:", err);
                setErrorTheme(err.message);
                // Use default theme on error
                setThemeData({
                    id: 'default',
                    colors: {
                        primary: '#3182CE',
                        secondary: '#2C5282',
                        accent: '#63B3ED',
                        background: '#F7FAFC',
                        text: '#1A202C',
                    }
                });
            } finally {
                setLoadingTheme(false);
            }
        };

        fetchTheme();
    }, [subdomain, API_BASE_URL]);

    // Create Chakra UI theme based on fetched theme data
    const chakraTheme = useMemo(() => {
        if (!themeData) {
            // Return default theme while loadingTheme
            return extendTheme({
                config: {
                    initialColorMode: 'light',
                    useSystemColorMode: false,
                },
                styles: {
                    global: {
                        html: {
                            scrollBehavior: 'smooth',
                        },
                    },
                },
            });
        }

        return extendTheme({
            config: {
                initialColorMode: themeData.id === 'midnight' ? 'dark' : 'light',
                useSystemColorMode: false,
            },
            colors: {
                brand: {
                    50: themeData.colors.background,
                    100: themeData.colors.accent,
                    500: themeData.colors.primary,
                    700: themeData.colors.secondary,
                    900: themeData.colors.text,
                },
            },
            styles: {
                global: {
                    html: {
                        scrollBehavior: 'smooth',
                    },
                    body: {
                        bg: themeData.colors.background,
                        color: themeData.colors.text,
                    },
                },
            },
            components: {
                Button: {
                    defaultProps: {
                        colorScheme: 'brand',
                    },
                },
                Link: {
                    baseStyle: {
                        color: themeData.colors.primary,
                    },
                },
            },
        });
    }, [themeData]);

    return (
        <ThemeContext.Provider
            value={{
                themeData,
                logo,
                businessName,
                loadingTheme,
                errorTheme,
                chakraTheme,
                subdomain
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;